import { createContext, useContext, useMemo } from 'react'

import { Feature } from '@/schemas/feature'

type ContextFeaturedState = {
  features: Feature[]
}

type ContextFeaturedProps = {
  children: React.ReactNode
}

type FeaturedProps = {
  feature: Feature
  fallback?: React.ReactNode
  children: React.ReactNode
}

function envFeatures(): Feature[] {
  const rawFeatures = import.meta.env.VITE_FEATURES

  if (rawFeatures) {
    try {
      const parsedFeatures = JSON.parse(rawFeatures)

      if (Array.isArray(parsedFeatures)) {
        return parsedFeatures
      }

      return []
    } catch {
      return []
    }
  }

  return []
}

const FeaturedContext = createContext<ContextFeaturedState>({ features: [] })

function FeaturedContextProvider({ children }: ContextFeaturedProps) {
  const features = useMemo(() => envFeatures(), [])

  return (
    <FeaturedContext.Provider value={{ features }}>
      {children}
    </FeaturedContext.Provider>
  )
}

function useFeaturedContext() {
  const context = useContext(FeaturedContext)

  if (!context) {
    throw new Error(
      'useFeaturedContext can only be used inside FeaturedContextProvider',
    )
  }

  return context
}

function useFeatured(featureToValidate: Feature) {
  const { features } = useFeaturedContext()

  const isAllowed = features.includes(featureToValidate)

  return { isAllowed }
}

function Featured({ feature, fallback, children }: FeaturedProps) {
  const { isAllowed } = useFeatured(feature)

  if (isAllowed) {
    return children
  }

  return fallback || false
}

export { FeaturedContextProvider, useFeaturedContext, useFeatured, Featured }
